import React, { useState, useEffect } from "react";
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Box,
  Grid,
  Typography,
  InputAdornment,
} from "@mui/material";
import { SingleSelectType } from "../../table/Filter";

import {
  getActiveBusinessEntity,
  merchantAction,
  Log,
  sendDataWithPost,
  getCreateAccOptions,
} from "../../../services/services";
import { useFormik } from "formik";
import * as yup from "yup";
import { isNumericInput, scrollToFieldError, trimValues } from "../../../utils/appUtils";
import FormDialog from "../../FormDialog";
import Select from "react-select";
import { DEFAULT_MIN_PAYOUT } from "../../../utils/constants";
// import { MAPPED_REGISTERED_COUNTRY } from "../../../utils/constants";

function CreateDialog({ open, onClose }) {
  const [options, setOptions] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoaded, setBankAccountsLoaded] = useState(false);
  const [staticOptions, setStaticOptions] = useState("");
  const [staticOptLoaded, setStaticOptLoaded] = useState(false);

  const {
    handleChange,
    handleSubmit,
    handleBlur,
    errors,
    values,
    touched,
    isSubmitting,
    resetForm,
    isValid,
    submitCount,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: "",
      business_entity: "",
      // registered_country: "",
      oxygen7_entity: "",
      merchant_category_code: "",
      address_line1: "",
      address_line2: "",
      address_line3: "",
      // address_line4: "",
      // address_line5: "",
      city: "",
      postal_code: "",
      address_as_business_entity: false,
      can_void: false,
      can_refund: false,
      can_merchant_void: false,
      can_merchant_refund: false,
      bank_account: "",
      min_payout_amount: [],
    },
    validationSchema: yup.object().shape({
      name: yup.string().required("Required field").trim(),
      business_entity: yup.string().required("Required field"),
      // registered_country: yup.string().required(),
      oxygen7_entity: yup.string().required("Required field"),
      merchant_category_code: yup.string().required("Required field"),
      bank_account: yup.string().required("Required field"),
      address_line1: yup.string().when("address_as_business_entity", {
        is: false,
        then: yup.string().required("Required field"),
      }).trim(),
      min_payout_amount: yup.array().of(
        yup.object().shape({
          min_amount: yup
            .number()
            .typeError("Must be a number")
            .positive("Must be a positive number")
            .max(999999999999, "Must be smaller than 999,999,999,999")
            .required("Required field"),
        })
      ),
    }),
    onSubmit: async (values) => {
      const newValues = trimValues(values);
      if (newValues.min_payout_amount && newValues.min_payout_amount.length === 0) {
        newValues.min_payout_amount = null;
      }
      Log.info("Click button [Add Merchant] - Add new merchant", newValues);
      await merchantAction(
        "ADD",
        {
          data: newValues,
          cb: onClose,
        },
        null
      );
    },
  });

  const setDataBankAccounts = (res) => {
    const newData = res?.bank_accounts?.map((item) => item);
    setBankAccounts(newData);
  }

  const getBankAccounts = async (business_entity_id) => {
    const url = `/merchant/merchant/bank_accounts_by_business_entity/`;
    sendDataWithPost(url, { business_entity_id }, setDataBankAccounts, setBankAccountsLoaded);
  }

  useEffect(() => {
    getActiveBusinessEntity(setOptions, setLoaded);
    getCreateAccOptions(setStaticOptions, setStaticOptLoaded);
  }, []);

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  useEffect(() => {
    if (isValid) return;
    scrollToFieldError(errors);
  }, [submitCount, isValid]);

  if (!open) return <></>;
  //Dialog Stuff

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Add New Merchant"
      handleSubmit={handleSubmit}
      loading={isSubmitting}
      maxWidth="lg"
    >
      <form noValidate onSubmit={handleSubmit}>
        <Grid container spacing={4} sx={{ py: 2, px: 2 }}>
          <Grid item sm={6} xs={12}>
            <Typography variant="h6" my={1}>
              Merchant Information
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">Merchant Name*</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <TextField
                  label="Merchant Name"
                  placeholder="Merchant Name"
                  name="name"
                  fullWidth
                  margin="dense"
                  size="small"
                  color="secondary"
                  value={values.name}
                  onChange={(e) => {
                    const uppercasedValue = e.target.value.toUpperCase();
                    setFieldValue("name", uppercasedValue)
                  }}
                  onBlur={handleBlur}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            {loaded && (
              <Grid container spacing={2} alignItems="center">
                <Grid item md={4} xs={4}>
                  <Typography align="right">Business Entity*</Typography>
                </Grid>
                <Grid item md={8} xs={8}>
                  <Autocomplete
                    options={options?.business_entity?.map((entity) => ({
                      id: entity.id,
                      label: entity.business_name,
                    }))}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    )}
                    onChange={(e, newValue) => {
                      setFieldValue("business_entity", newValue?.id ?? "");
                      getBankAccounts(newValue?.id ?? "");
                    }}
                    fullWidth
                    size="small"
                    disabled={isSubmitting}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Business Entity"
                        placeholder="Business Entity"
                        margin="dense"
                        color="secondary"
                        error={Boolean(touched.business_entity && errors.business_entity)}
                        helperText={touched.business_entity && errors.business_entity}
                        name="business_entity"
                      />
                    )}
                    isOptionEqualToValue={(option) => option.id === values.business_entity}
                  />
                </Grid>
              </Grid>
            )}
            {/* {bankAccounts.length > 0 && ( */}
            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">Bank Account*</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <Autocomplete
                  options={bankAccounts?.map((item) => ({
                    id: item.id,
                    label: `${item.account_number} (${item.bank})`,
                  }))}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )}
                  onChange={(e, newValue) => {
                    setFieldValue("bank_account", newValue?.id ?? "");
                  }}
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bank Account"
                      placeholder="Bank Account"
                      margin="dense"
                      color="secondary"
                      error={Boolean(touched.bank_account && errors.bank_account)}
                      helperText={touched.bank_account && errors.bank_account}
                      name="bank_account"
                    />
                  )}
                  isOptionEqualToValue={(option) => option.id === values.bank_account}
                />
                {/* )} */}
              </Grid>
            </Grid>
            {/*
        <SingleSelectType
          id="true"
          error={Boolean(
            touched.registered_country && errors.registered_country
          )}
          name="registered_country"
          options={options?.registered_country?.map((country) => ({
            id: country.id,
            name: MAPPED_REGISTERED_COUNTRY[country.registered_country],
          }))}
          label="Registered Country"
          selected={values.registered_country}
          onChange={handleChange}
          disabled={isSubmitting}
        />
        */}
            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">Merchant Category Code*</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <Autocomplete
                  options={options?.mcc?.map((mcc) => ({
                    id: mcc.id,
                    label: mcc.code_description,
                  }))}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )}
                  onChange={(e, newValue) => {
                    setFieldValue("merchant_category_code", newValue?.id ?? "");
                  }}
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Merchant Category Code"
                      placeholder="Merchant Category Code"
                      margin="dense"
                      color="secondary"
                      error={Boolean(touched.merchant_category_code && errors.merchant_category_code)}
                      helperText={touched.merchant_category_code && errors.merchant_category_code}
                      name="merchant_category_code"
                    />
                  )}
                  isOptionEqualToValue={(option) => option.id === values.merchant_category_code}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">Oxygen7 Entity*</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <Autocomplete
                  options={(staticOptions?.oxygen7_entity || [])?.map((entity) => ({
                    id: entity.id,
                    label: entity.entity,
                  })) || []}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  )}
                  onChange={(e, newValue) => {
                    setFieldValue("oxygen7_entity", newValue?.id ?? "");
                  }}
                  fullWidth
                  size="small"
                  disabled={isSubmitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Oxygen7 Entity"
                      placeholder="Oxygen7 Entity"
                      margin="dense"
                      color="secondary"
                      error={Boolean(touched.oxygen7_entity && errors.oxygen7_entity)}
                      helperText={touched.oxygen7_entity && errors.oxygen7_entity}
                      name="oxygen7_entity"
                    />
                  )}
                  isOptionEqualToValue={(option) => option.id === values.oxygen7_entity}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item sm={6} xs={12}>
            <Typography variant="h6" my={1}>
              Payment Settings
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="can_void"
                    checked={values.can_void}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                }
                label="Can Void"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="can_refund"
                    checked={values.can_refund}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                }
                label="Can Refund"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="can_merchant_void"
                    checked={values.can_merchant_void}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                }
                label="Can Void via Merchant Portal"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="can_merchant_refund"
                    checked={values.can_merchant_refund}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                }
                label="Can Refund via Merchant Portal"
              />
            </Box>
          </Grid>

          <Grid item sm={6} xs={12}>
            <Typography variant="h6" my={1}>
              Address Details
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">Address 1{values.address_as_business_entity ? "" : "*"}</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <TextField
                  label="Address 1"
                  placeholder="Address 1"
                  name="address_line1"
                  fullWidth
                  margin="dense"
                  size="small"
                  color="secondary"
                  value={values.address_line1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.address_line1 && errors.address_line1)}
                  helperText={touched.address_line1 && errors.address_line1}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">Address 2</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <TextField
                  label="Address 2"
                  placeholder="Address 2"
                  name="address_line2"
                  fullWidth
                  margin="dense"
                  size="small"
                  color="secondary"
                  value={values.address_line2}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">Address 3</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <TextField
                  label="Address 3"
                  placeholder="Address 3"
                  name="address_line3"
                  fullWidth
                  margin="dense"
                  size="small"
                  color="secondary"
                  value={values.address_line3}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
            {/* <TextField
          label="Address line 4"
          name="address_line4"
          fullWidth
          variant="standard"
          value={values.address_line4}
          onChange={handleChange}
          sx={{ my: 2 }}
          disabled={isSubmitting}
        />

        <TextField
          label="Address line 5"
          name="address_line5"
          fullWidth
          variant="standard"
          value={values.address_line5}
          onChange={handleChange}
          sx={{ my: 2 }}
          disabled={isSubmitting}
        /> */}
            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">City</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <TextField
                  label="City"
                  placeholder="City"
                  name="city"
                  fullWidth
                  margin="dense"
                  size="small"
                  color="secondary"
                  value={values.city}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center">
              <Grid item md={4} xs={4}>
                <Typography align="right">Postal Code</Typography>
              </Grid>
              <Grid item md={8} xs={8}>
                <TextField
                  label="Postal Code"
                  placeholder="Postal Code"
                  name="postal_code"
                  fullWidth
                  size="small"
                  margin="dense"
                  color="secondary"
                  value={values.postal_code}
                  onChange={e => {
                    if (isNumericInput(e.target.value)) {
                      handleChange(e);
                    }
                  }}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>

            <Box sx={{ pl: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="address_as_business_entity"
                    checked={values.address_as_business_entity}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                }
                label="Address as Business Entity"
              />
            </Box>
          </Grid>

          <Grid item sm={6} xs={12}>
            <Typography variant="h6" my={1}>
              Minimum Payout Amount
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Select
                id="currency_list"
                name="currency_list"
                getOptionLabel={option => option.currency}
                getOptionValue={option => option.id}
                closeMenuOnSelect={false}
                isMulti
                options={DEFAULT_MIN_PAYOUT.map(c => ({ id: c.currency, currency: c.currency }))}
                value={(values.min_payout_amount || []).map(c => ({ id: c.currency, currency: c.currency }))}
                onChange={selected => {
                  const selectedArray = selected || [];
                  const newConfig = selectedArray.map(option => {
                    const existing = (values.min_payout_amount || []).find(c => c.currency === option.id);
                    if (existing) return existing;

                    const defaultAmt = DEFAULT_MIN_PAYOUT.find(d => d.currency === option.id)?.min_amount || "";
                    return { currency: option.id, min_amount: defaultAmt };
                  });
                  setFieldValue('min_payout_amount', newConfig);
                }}
                styles={{
                  control: provided => ({ ...provided, minHeight: "40px", fontSize: "16px" }),
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  placeholder: provided => ({ ...provided, fontSize: "16px" }),
                  singleValue: provided => ({ ...provided, fontSize: "16px" }),
                  option: provided => ({ ...provided, fontSize: "16px" })
                }}
                menuPortalTarget={document.body}
              />

              {values.min_payout_amount?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {(values.min_payout_amount || []).map((item, index) => {
                    const fieldName = `min_payout_amount[${index}].min_amount`;
                    const fieldError = errors?.min_payout_amount?.[index]?.min_amount;
                    const fieldTouched = touched?.min_payout_amount?.[index]?.min_amount;
                    const minAmtError = Boolean(fieldTouched && fieldError);

                    return (
                      <TextField
                        key={item.currency}
                        sx={{ m: 1 }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {item.currency}
                            </InputAdornment>
                          ),
                        }}
                        label="Min Amount"
                        id={fieldName}
                        name={fieldName}
                        value={item.min_amount}
                        onChange={(e) => {
                          const newConfig = [...(values.min_payout_amount || [])];
                          newConfig[index] = { ...newConfig[index], min_amount: e.target.value === '' ? '' : parseFloat(e.target.value) };
                          setFieldValue('min_payout_amount', newConfig);
                        }}
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        error={minAmtError}
                        helperText={minAmtError ? fieldError : ""}
                        variant="standard"
                        disabled={isSubmitting}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormDialog>
  );
}

export default CreateDialog;
